import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { finalize, of } from 'rxjs';
import { TabSection } from '../../models';
import { NoDataMessage } from '../../shared/no-data-message/no-data-message';
import { SidePanel } from '../../shared/side-panel/side-panel';
import { TranslationDirective } from '../../shared/translation-directive';
import { ToastService } from '../../toast-service';
import { NotificationCard } from './notification-card/notification-card';
import { NotificationStore } from './notification-store';

type FilterBy = 'all' | 'unread';

@Component({
  selector: 'upu-notification-panel',
  imports: [
    SidePanel,
    TranslationDirective,
    NotificationCard,
    NoDataMessage,
    MatProgressSpinner,
  ],
  templateUrl: './notification-panel.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationPanel {
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationStore = inject(NotificationStore);

  protected readonly openPanel = this.notificationStore.openPanel;
  protected readonly unreadCount = this.notificationStore.unreadCount.value;
  protected readonly filterBy = signal<FilterBy>('all');
  protected readonly spinnerSize = Array(10);

  protected readonly notifications = rxResource({
    params: () => ({ openPanel: this.openPanel(), filterBy: this.filterBy() }),
    stream: ({ params }) =>
      !params.openPanel
        ? of([])
        : this.notificationStore.getNotifications(params.filterBy === 'unread'),
    defaultValue: [],
  });

  protected readonly loading = signal(false);

  protected tabs: TabSection<FilterBy>[] = [
    {
      name: 'all',
      label: 'notificationPanel.all',
    },
    {
      name: 'unread',
      label: 'notificationPanel.unread',
    },
  ];

  protected markAllAsRead(): void {
    const loadingTimeout = setTimeout(() => this.loading.set(true), 1000);

    this.notificationStore
      .markAllAsRead()
      .pipe(
        finalize(() => {
          clearTimeout(loadingTimeout);
          this.loading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          clearTimeout(loadingTimeout);
          this.notifications.reload();
        },
        error: () => {
          this.toast.show({
            type: 'error',
            title: 'notificationPanel.toggleReadError.title',
            message: 'notificationPanel.toggleReadError.message',
          });

          clearTimeout(loadingTimeout);
        },
      });
  }
}
